import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { HIGH_RESOLUTION_THRESHOLD } from '@racemap/utilities/consts/events';
import { eventTypesByType } from '@racemap/utilities/consts/eventTypes';
import { createPatchObj } from '@racemap/utilities/functions/createPatchObj';
import { getSplitsOfTrack } from '@racemap/utilities/functions/event';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Modal } from 'antd';
import { type Immutable, produce } from 'immer';
import { InterpolationIntervalConfirmModalContent } from 'src/components/InterpolationIntervalConfirmModalContent';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { Tristate } from '../../../components/utils/tristateTask';
import { settingKey, trackEvent } from '../../../lib/googleAnalytics';
import type { DraftState, State } from '../../reducers';
import {
  eventIsLocked,
  needsAConfirmation,
  presentConfirmationDialog,
  presentEventLockedDialog,
} from '../events_reducer_helper';
import type { CurrentEvent, EventsActions } from '../events_reducers';
import { logAndToast, type SetState, setSavingState, withSavingState } from './helpers';

const apiClient = RacemapAPIClient.fromWindowLocation();

type StoredEvent = Immutable<RacemapEvent | CurrentEvent>;

// fields the patch object never carries
const PROTECTED_FIELDS = ['id', 'createdAt', 'updatedAt', 'keys', 'billableItems', 'childEvents'];

const lowersStepWidthBelowHighResolution = (
  patchObj: Partial<RacemapEvent>,
  previous: StoredEvent,
) =>
  patchObj.playerOptions?.stepWidth != null &&
  patchObj.playerOptions.stepWidth < HIGH_RESOLUTION_THRESHOLD &&
  previous.playerOptions.stepWidth >= HIGH_RESOLUTION_THRESHOLD;

const confirmInterpolationInterval = () =>
  new Promise<void>((resolve, reject) => {
    Modal.confirm({
      title: 'Warning',
      content: <InterpolationIntervalConfirmModalContent />,
      onOk: () => resolve(),
      onCancel: () => reject(new Error('CANCEL')),
    });
  });

// Asks the user before the current event is saved. Rejects with CANCEL or LOCKED, or resolves
// false when the update must stop without an error.
async function confirmUpdateOfCurrentEvent(
  patchObj: Partial<RacemapEvent>,
  previous: StoredEvent,
): Promise<boolean> {
  if (eventIsLocked(patchObj, previous)) {
    await presentEventLockedDialog(previous);
    return false;
  }

  if (needsAConfirmation(patchObj, previous)) {
    await presentConfirmationDialog(patchObj, previous);
  }

  if (lowersStepWidthBelowHighResolution(patchObj, previous)) {
    await confirmInterpolationInterval();
  }
  return true;
}

// if the sport of the event changes and we have 2 splits (start and finish)
// and the first split has no sport yet, seed the first split's sport
async function seedSportOfFirstSplit(
  event: StoredEvent,
  eventType: NonNullable<RacemapEvent['eventType']>,
) {
  const splits = getSplitsOfTrack(event);
  if (splits == null || splits.length !== 2 || splits[0].properties.newSport !== '') return;

  // The event is already stored, so a failure here is partial: reporting it as a failed
  // update would roll the local event back to a state the server no longer holds.
  try {
    await apiClient.updateGeoPoint(splits[0].id, {
      ...splits[0],
      properties: {
        ...splits[0].properties,
        newSport: eventType,
      },
    });
  } catch (error) {
    console.error(error);
    toast.warn(
      'The event type was saved, but the first split kept its old sport. Set it there manually.',
    );
  }
}

// Rolls the optimistic update back. A declined confirmation or a locked event is no failure.
function handleEventUpdateError(
  set: SetState,
  eventId: string,
  previous: StoredEvent | undefined,
  error: unknown,
) {
  const userCancelUpdate = error instanceof Error && error.message === 'CANCEL';
  const eventIsLocked = error instanceof Error && error.message === 'LOCKED';
  const isDeclined = userCancelUpdate || eventIsLocked;

  set(
    produce((s) => {
      if (previous != null) s.events.items.set(previous.id, previous);
      s.events.savingState = isDeclined ? Tristate.NORMAL : Tristate.FAILURE;
    }),
  );

  if (userCancelUpdate) toast.warn('The confirmation was declined. The changes were not saved.');
  if (eventIsLocked) toast.warn('The event times are locked. The changes were not saved.');
  if (isDeclined) return;

  console.error(`Failed to change a event: ${eventId}`);
  console.error(error);
  const serverMessage =
    error instanceof HTTPFetchError && error.serverError != null ? error.serverError : null;
  toast.error(
    serverMessage != null
      ? `Failed to update the event: ${serverMessage}`
      : `The attempt to update the event ${eventId} failed.`,
  );
}

export const createEventActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<EventsActions, 'addEvent' | 'updateEvent' | 'deleteEvent'> => ({
  addEvent: (event) =>
    withSavingState(
      set,
      async () => {
        const createdEvent = await apiClient.createEvent(event);
        set(
          produce((s) => {
            s.events.items.set(createdEvent.id, createdEvent);
          }),
        );
        return createdEvent;
      },
      logAndToast('Failed to add a event', 'The attempt to add the event failed.'),
    ),
  updateEvent: async (eventId, updates) => {
    let currentEventState = get().events.items.get(eventId);
    try {
      if (currentEventState == null) {
        currentEventState = await get().events.actions.loadEvent(eventId);
      }

      if (currentEventState == null) {
        throw new Error(`Fail Event Update. Event with eventId ${eventId} is unknown.`);
      }

      const eventIdOfCurrentEvent = get().events.currentEventId;
      const isCurrent = eventId === eventIdOfCurrentEvent;
      const isChildOfCurrent = currentEventState.parent === eventIdOfCurrentEvent;

      // create the patch object, that only consist with the updates
      const patchObj = createPatchObj(updates, currentEventState as RacemapEvent, PROTECTED_FIELDS);

      if (patchObj.eventType != null) {
        patchObj.maxSpeed = eventTypesByType[patchObj.eventType].maxSpeed;
      }

      // Optimistic update
      set(
        produce((s: DraftState) => {
          s.events.savingState = Tristate.SPINNING;
          const currentEvent = s.events.items.get(currentEventState?.id || '');

          if (currentEvent != null && (isCurrent || isChildOfCurrent)) {
            s.events.items.set(currentEvent.id, {
              ...currentEvent,
              ...(patchObj as RacemapEvent),
            });
          }
        }),
      );

      if (isCurrent && !(await confirmUpdateOfCurrentEvent(patchObj, currentEventState))) return;

      await apiClient.updateEvent(eventId, patchObj);
      for (const { key } of updates) trackEvent('event_setting_change', { key: settingKey(key) });

      if (patchObj.eventType != null) {
        await seedSportOfFirstSplit(currentEventState, patchObj.eventType);
      }

      // reload current Event
      if (eventIdOfCurrentEvent != null && (isCurrent || isChildOfCurrent)) {
        await get().events.actions.loadCurrentEvent(eventIdOfCurrentEvent);
      }

      setSavingState(set, Tristate.NORMAL);
    } catch (error) {
      handleEventUpdateError(set, eventId, currentEventState, error);
    }
  },
  deleteEvent: (eventId) =>
    withSavingState(
      set,
      async () => {
        await apiClient.removeEvent(eventId);
        set(
          produce((s) => {
            s.events.items.delete(eventId);
          }),
        );
      },
      logAndToast('Failed to remove a event', `The attempt to delete the event ${eventId} failed.`),
    ),
});
